import * as _ from 'lodash';
import VueI18n from 'vue-i18n';

import insectIds from 'Data/insect_ids.json';
import insectIdsCrawling from 'Data/insect_ids_crawling.json';
import insectDetails from 'Data/insect_details.json';

const SYMBOLS = insectIds.versions;

export const LATEST_VERSION = Math.max(...Object.keys(SYMBOLS).map(v => parseInt(v, 10)));

// 個体の種別（target_locomotion の値）
export const LOCOMOTION_FLYING = 'flying';
export const LOCOMOTION_CRAWLING = 'crawling';
// 名前解決の収録範囲（個体種別と値を被らせない別語彙）
export const LOCOMOTION_SCOPE = { FLYING_ONLY: 'flying_only', ALL: 'flying_and_crawling' };

// target_locomotion は flying / crawling / 未定義（未定義は flying 扱い、BE 仕様に一致）
export const locomotionOf = event =>
  (event.target_locomotion === LOCOMOTION_CRAWLING ? LOCOMOTION_CRAWLING : LOCOMOTION_FLYING);

// event の locomotion と feature toggle から locomotionScope を導出する純粋関数
export const calculateLocomotionScope = (event, featureIsActive) => {
  if (!featureIsActive('s_type_crawling_pests')) {
    return LOCOMOTION_SCOPE.FLYING_ONLY;
  }
  return locomotionOf(event) === LOCOMOTION_CRAWLING
    ? LOCOMOTION_SCOPE.ALL
    : LOCOMOTION_SCOPE.FLYING_ONLY;
};

// 虫種の多言語ラベル定義（ja/en）
const messages = {
  en: {
    aburamushi: 'Aphids',
    azamiuma: 'Thrips',
    chatatemushi_rui: 'Booklice (winged)',
    choubae_ka: 'Moth flies',
    funkobae_ka: 'Small dung flies',
    gaganbo_rui: 'Crane flies',
    haneari_rui: 'Winged ants',
    hanekakushi_ka: 'Rove beetles',
    hayatobibae_ka: 'Sphaeroceridae',
    ka_ka: 'Mosquitoes',
    katsuobushimushi_ka: 'Carpet beetles',
    kinokobae: 'Fungus gnats',
    kogatahachi_rui: 'Parasitic wasps',
    kurobanekinokobae_ka: 'Dark winged fungus gnats',
    nisekebae_ka: 'Minute black scavenger flies',
    nomibae_ka: 'Humpbacked flies',
    noshimemadarameiga: 'Plodia interpunctella',
    nukaka_ka: 'Biting midges',
    oogatabae_rui: 'Large flies',
    shibanmushi_ka: 'Tobacco beetles, Drugstore beetles',
    shoga_rui: 'Small moths',
    shoujoubae_ka: 'Fruit flies',
    tamabae_ka: 'Gall midges',
    tobigera_moku: 'Caddisflies',
    togehanebae: 'Heleomyzid flies',
    unnka_yokobai: 'Planthoppers and leafhoppers',
    yusurika_ka: 'Non-biting midges',
    chabanegoki: 'German cockroach adults',
    chabanegokio: 'German cockroach large-nymphs',
    chabanegokiy: 'German cockroach early-nymphs',
    kurogoki: 'Smoky brown/American cockroach adults',
    kurogokio: 'Smoky brown/American cockroach large-nymphs',
    kurogokiy: 'Smoky brown/American cockroach early-nymphs',
    dani: 'Mites (indoor)',
    kumo: 'Spiders',
    chatateless: 'Booklice (wingless)',
    dango: 'Pill bugs, Sow bugs',
    ari: 'Ants (wingless)',
    takaradani: 'Mites (outdoor)',
    tobi: 'Springtails',
    others: 'Other',
  },
  ja: {
    aburamushi: 'アブラムシ科',
    azamiuma: 'アザミウマ目',
    chatatemushi_rui: 'チャタテムシ類（有翅）',
    choubae_ka: 'チョウバエ科',
    funkobae_ka: 'フンコバエ科',
    gaganbo_rui: 'ガガンボ類',
    haneari_rui: 'アリ科（有翅）',
    hanekakushi_ka: 'ハネカクシ科',
    hayatobibae_ka: 'ハヤトビバエ',
    ka_ka: 'カ科',
    katsuobushimushi_ka: 'カツオブシムシ科',
    kogatahachi_rui: 'ハチ類（小型）',
    kinokobae: 'キノコバエ科',
    kurobanekinokobae_ka: 'クロバネキノコバエ科',
    nisekebae_ka: 'ニセケバエ科',
    nomibae_ka: 'ノミバエ科',
    noshimemadarameiga: 'ノシメマダラメイガ',
    nukaka_ka: 'ヌカカ科',
    oogatabae_rui: 'ハエ類（大型）',
    shibanmushi_ka: 'シバンムシ科',
    shoga_rui: 'ガ類（小型）',
    shoujoubae_ka: 'ショウジョウバエ科',
    tamabae_ka: 'タマバエ科',
    tobigera_moku: 'トビケラ目',
    togehanebae: 'トゲハネバエ科',
    unnka_yokobai: 'ウンカ・ヨコバイ類',
    yusurika_ka: 'ユスリカ科',
    chabanegoki: 'チャバネゴキブリ成虫',
    chabanegokio: 'チャバネゴキブリ老齢幼虫',
    chabanegokiy: 'チャバネゴキブリ若齢幼虫',
    kurogoki: 'クロ/ワモンゴキブリ成虫',
    kurogokio: 'クロ/ワモンゴキブリ老齢幼虫',
    kurogokiy: 'クロ/ワモンゴキブリ若齢幼虫',
    dani: 'ダニ類（屋内性）',
    kumo: 'クモ目',
    chatateless: 'チャタテムシ類（無翅）',
    dango: 'ダンゴムシ・ワラジムシ類',
    ari: 'アリ科（無翅）',
    takaradani: 'ダニ類（野外性）',
    tobi: 'トビムシ科',
    others: 'その他',
  },
};

const i18n = new VueI18n({ locale: 'en', messages });

const buildInsectType = ({ type, symbol }) => {
  const details = insectDetails[type] || { route: 'outdoor', causes: [] };
  return {
    type,
    symbol,
    route: details.route,
    causes: details.causes,
    locomotion: details.locomotion,
  };
};

// 虫種シンボル表から label 付きの虫種情報マップを構築する純粋関数
export const buildInsectTypeMap = (symbols, locale) =>
  _.mapValues(symbols, (symbol, type) => ({
    ...buildInsectType({ type, symbol }),
    label: i18n.t(type, locale),
  }));

// symbols に含まれない種を others に合算する純粋関数（合計頭数を保持）
export const normalizeInsectCounts = (insectCounts, symbols) =>
  _.reduce(insectCounts, (acc, count, type) => {
    const key = symbols[type] ? type : 'others';
    acc[key] = (acc[key] || 0) + count;
    return acc;
  }, {});

export const insectTypeListForVersion = (insectTypesVersion) => {
  const symbols = SYMBOLS[insectTypesVersion];
  return _.map(symbols, (symbol, type) => buildInsectType({ type, symbol }));
};

// 内部ヘルパー（version → symbols への解決）
const flyingSymbolsAt = (version) => SYMBOLS[version || LATEST_VERSION];
const allSymbolsAt = (version) => ({ ...flyingSymbolsAt(version), ...insectIdsCrawling });

const extractNames = (insectTypeMap) => _.mapValues(insectTypeMap, t => t.label);

const toOptionList = (insectTypeMap) =>
  _.map(insectTypeMap, ({ type, symbol, label }) => ({
    value: type,
    text: `${symbol}. ${label}`,
  }));

// 公開 API: 最新バージョン固定版（locale のみ受け取り）

export const flyingInsectTypeMap = (locale) =>
  buildInsectTypeMap(flyingSymbolsAt(LATEST_VERSION), locale);

export const allInsectTypeMap = (locale) =>
  buildInsectTypeMap(allSymbolsAt(LATEST_VERSION), locale);

export const flyingInsectTypeOptions = (locale) =>
  toOptionList(flyingInsectTypeMap(locale));

export const allInsectTypeOptions = (locale) =>
  toOptionList(allInsectTypeMap(locale));

export const flyingInsectTypeNames = (locale) =>
  extractNames(flyingInsectTypeMap(locale));

export const allInsectTypeNames = (locale) =>
  extractNames(allInsectTypeMap(locale));

export const normalizeFlyingCounts = (counts) =>
  normalizeInsectCounts(counts, flyingSymbolsAt(LATEST_VERSION));

export const normalizeAllCounts = (counts) =>
  normalizeInsectCounts(counts, allSymbolsAt(LATEST_VERSION));

// 公開 API: バージョン指定版（InsectDetectionsEditor 用）

export const flyingInsectTypeMapAt = (version, locale) =>
  buildInsectTypeMap(flyingSymbolsAt(version), locale);

export const allInsectTypeMapAt = (version, locale) =>
  buildInsectTypeMap(allSymbolsAt(version), locale);

export const flyingInsectTypeOptionsAt = (version, locale) =>
  toOptionList(flyingInsectTypeMapAt(version, locale));

export const allInsectTypeOptionsAt = (version, locale) =>
  toOptionList(allInsectTypeMapAt(version, locale));

export const normalizeFlyingCountsAt = (version, counts) =>
  normalizeInsectCounts(counts, flyingSymbolsAt(version));

export const normalizeAllCountsAt = (version, counts) =>
  normalizeInsectCounts(counts, allSymbolsAt(version));
